import styled, { css } from 'styled-components';
import { TransparentButton } from '../../../__core/Styles/Buttons/TransparentButton';
import { SecondaryModernButton } from '../../../__core/Styles/Buttons/SecondaryModernButton';
import { fadeIn } from '../../../__core/Themes/Animations';

export const MaterialPage = styled.div`
  margin-top: 40px;
`;

export const MaterialHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  animation: ${fadeIn} 0.5s;

  .non-scored-label {
    font-style: italic;
    color: ${({ theme }) => theme.UI_COLORS.medium_gray};
  }

  .status-icon {
    cursor: pointer;
  }

  .quiz-status {
    display: flex;
    align-items: center;
    & > span {
      margin-right: 0.5rem;
      font-size: ${({ theme }) => theme.TYPOGRAPHY.bodyTextSmall.fontSize};
    }
  }

  h1 {
    margin: 1rem 0;
    color: ${({ theme }) => theme.UI_COLORS.secondary};
    font-size: 2.4rem;
    font-weight: ${({ theme }) => theme.TYPOGRAPHY.h1.fontWeight};
    word-break: break-word;

    @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
      font-size: ${({ theme }) => theme.TYPOGRAPHY.h1.fontSize};
    }
  }
`;

export const MaterialsContainer = styled.div<{ sidebarCollapse: boolean }>`
  transition: all 0.3s ease;
  padding: 2rem;
  max-width: 780px;
  margin: 0 auto;
  height: calc(100vh - 200px);

  @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
    height: auto;
    padding: 2rem 2rem 0
      ${({ sidebarCollapse }) => (sidebarCollapse ? '110px' : '390px')};
  }
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.UI_COLORS.main_gray};
  animation: ${fadeIn} 0.5s;

  & > span {
    margin-right: 1rem;
    margin-bottom: 1rem;

    &:last-of-type {
      margin-right: 0;
    }
  }
`;

export const MaterialInHtml = styled.div`
  color: ${({ theme }) => theme.UI_COLORS.secondary};
  font-size: ${({ theme }) => theme.TYPOGRAPHY.bodyText.fontSize};
  font-weight: ${({ theme }) => theme.TYPOGRAPHY.bodyText.fontWeight};
  line-height: 2.6rem;
  overflow-x: auto;

  code {
    font-weight: 700;
    font-family: monospace;
    background-color: ${({ theme }) => theme.COLORS.inlineCodeBackground};
    border: 1px solid ${({ theme }) => theme.COLORS.inlineCodeBorder};
    padding: 0 5px;
    line-height: 2.1rem;
  }

  .line-number {
    user-select: none;
    position: absolute;
    left: 2rem;
    width: 3rem;
    text-align: right;
    display: inline-block;
    padding-right: 0.5rem;
    line-height: 2.1rem;
    font-size: ${({ theme }) => theme.FONT_SIZES.sourceLineNumber};
    color: ${({ theme }) => theme.COLORS.secondaryMiddle};
    border-right: 0.1rem solid ${({ theme }) => theme.COLORS.secondaryMiddle};

    & > span {
      color: ${({ theme }) => theme.COLORS.secondaryMiddle};
    }
  }

  .issue-selection-highlighter {
    background: #77e0ff;
  }

  .copy-to-clipboard {
    position: absolute;
    top: 1rem;
    right: 1rem;
    outline: none;
    display: flex;
    background-color: white;
    border: 2px solid ${({ theme }) => theme.UI_COLORS.primary};
    border-radius: 4px;
    padding: 0.4em;
    transition: background-color 0.3s ease;

    & > svg {
      fill: ${({ theme }) => theme.UI_COLORS.secondary};
    }

    &:hover {
      background-color: ${({ theme }) => theme.COLORS.primaryMiddle};
      cursor: pointer;
    }
  }

  div {
    overflow: auto;
  }

  img {
    cursor: zoom-in;
  }

  pre {
    padding: 2rem 4rem 2rem 7rem;
    background-color: ${({ theme }) => theme.UI_COLORS.main_gray};
    position: relative;

    code {
      font-weight: inherit;
      font-family: inherit;
      border: inherit;
      background-color: ${({ theme }) => theme.UI_COLORS.main_gray};
    }
  }

  blockquote {
    padding-left: 1rem;
    border-left: 0.3rem solid ${({ theme }) => theme.COLORS.primaryMiddle};
    opacity: 0.8;
    margin: 2.5rem 0;
    line-height: 1.4em;
  }

  p {
    margin: 1rem 0;
    font-size: ${({ theme }) => theme.FONT_SIZES.article};
  }

  ol,
  ul {
    margin-top: 1rem;
  }

  h1 {
    padding-bottom: 1rem;
    margin-top: 2rem;
    line-height: 1.5;
    font-size: ${({ theme }) => theme.TYPOGRAPHY.h1.fontSize};
    font-weight: ${({ theme }) => theme.TYPOGRAPHY.h1.fontWeight};
  }

  h2 {
    font-size: ${({ theme }) => theme.TYPOGRAPHY.h2.fontSize};
    font-weight: bold;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 2.2rem;
    margin-bottom: 1rem;
  }

  img {
    margin-left: auto;
    margin-right: auto;
    display: block;
    max-width: 100%;
  }

  table {
    margin: 0 auto;
    border-collapse: collapse;
    display: block;
    overflow: auto;

    td,
    th {
      padding: 1rem 1.5rem;
    }

    td {
      color: ${({ theme }) => theme.COLORS.secondary};
      border: 1px solid ${({ theme }) => theme.COLORS.elementBorder};
      background-color: ${({ theme }) => theme.COLORS.elementOdd};
    }

    th {
      color: ${({ theme }) => theme.COLORS.textStandard};
      border: 1px solid ${({ theme }) => theme.COLORS.elementBorder};
      font-weight: normal;
      background-color: ${({ theme }) => theme.COLORS.secondaryMiddle};
    }
  }
`;

export const MaterialBox = styled.div`
  animation: ${fadeIn} 0.5s;

  @media (${({ theme }) => theme.MEDIA_MAX.tablet}) {
    padding: 1rem 0 0 0;
  }
`;

export const SurveyStyle = styled.div`
  margin-bottom: 1rem;

  .quiz-info {
    margin: 2rem 0;
    font-size: ${({ theme }) => theme.FONT_SIZES.primary};
    color: ${({ theme }) => theme.COLORS.secondary};
  }
`;

export const ActionsSection = styled.div`
  padding: 2rem 0;
  display: flex;
  justify-content: flex-end;

  & > button,
  & > a {
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.COLORS.secondary};

    &:hover {
      box-shadow: inset 0 0 0 3px ${({ theme }) => theme.COLORS.secondary};
    }
  }
`;

export const PresentationButton = styled(TransparentButton)`
  color: ${({ theme }) => theme.UI_COLORS.secondary};
  font-size: ${({ theme }) => theme.TYPOGRAPHY.label.fontSize};
  font-weight: bold;
  display: flex;
  flex-wrap: nowrap;
  text-align: right;

  .label {
    width: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    text-wrap: none;
    white-space: nowrap;
  }

  &:hover {
    .label {
      padding-right: 1rem;
      width: 65px;
    }
  }
`;

export const PresentationContainer = styled.div`
  position: relative;

  .react-pdf__Page > canvas,
  .react-pdf__Page > .react-pdf__Page__textContent {
    margin: 0 auto;
  }
  #presentation-container {
    position: relative;
    outline: none;

    .presentations-action {
      display: block;
      position: absolute;
      right: 10px;
      top: 10px;

      &-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
    }

    button.slideControl {
      position: absolute;
      border: none;
      outline: none;
      background-color: transparent;
      bottom: 10%;
      color: ${({ theme }) => theme.UI_COLORS.primary};
      stroke: ${({ theme }) => theme.UI_COLORS.secondary};
      stroke-width: 1rem;
      font-size: 3rem;
      text-shadow: rgb(0 0 0) 0 0 3px;

      &--left {
        left: 10px;
      }
      &--right {
        right: 10px;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  #presentation-container:fullscreen {
    .react-pdf__Document {
      & > .react-pdf__Page {
        & > canvas {
          width: 100vw !important;
          height: auto !important;
          margin-top: 50%;
        }
      }
    }

    @media (${({ theme }) => theme.MEDIA_MIN.mobile}) {
      .react-pdf__Document {
        height: 100vh;
        user-select: none;
        & > .react-pdf__Page {
          width: auto;
          height: 100%;

          & > canvas {
            width: auto !important;
            height: 100% !important;
            margin: 0 auto;
          }
        }
      }
    }
  }
`;

export const PresentationIframeContainer = styled.div`
  width: 100%;
  max-width: 980px;
  transition: all 0.2s ease;

  &.fullscreen {
    z-index: 9999;
    width: 100%;
    max-width: 100vw;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    & > iframe {
      height: 100vh;
      max-height: none;
    }

    & > .presentations-action {
      display: block;
      position: absolute;
      right: 10px;
      top: 65px;
    }
  }

  & > iframe {
    width: 100%;
    border: none;
  }

  .presentations-action {
    display: flex;
    align-items: center;
    justify-content: space-around;

    @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
      position: absolute;
      right: 10px;
      top: 40px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    & > div {
      text-align: right;
    }
  }
`;

export const PresentationMask = styled.div`
{
  position: absolute;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  box-sizing: border-box;
  color: rgb(200, 200, 200);
  font-size: 2.8rem;
  line-height: 4rem;
  text-align: center;
  flex-direction: column;
  z-index: 2;

  svg {
    margin-top: 1.5rem;
    height: 40px;
    fill: white;
    animation: pulse 2s;
    animation-iteration-count: 2;
  }

  @media (${({ theme }) => theme.MEDIA_MIN.mobile}) {
    display: none;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }

    50% {
      transform: scale(0.7);
    }

    100% {
      transform: scale(1);
    }
  }
`;

export const CardRow = styled.div`
  padding: 2rem 0;
  & > a {
    margin: 0 auto;
  }
`;

export const RateBar = styled.div`
  padding: 2rem 0;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.UI_COLORS.main_gray};
  border-top: 1px solid ${({ theme }) => theme.UI_COLORS.main_gray};
  animation: ${fadeIn} 0.5s;

  .issue-section {
    display: none;
  }

  .issue-section > svg {
    margin-right: 1rem;
    fill: ${({ theme }) => theme.UI_COLORS.secondary};
  }

  & > div {
    display: flex;
    align-items: center;

    .rate-label {
      margin-right: 1rem;
      font-size: 1.8rem;
    }

    & > div.star {
      svg {
        fill: ${({ theme }) => theme.UI_COLORS.secondary};
        cursor: pointer;
      }

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
    justify-content: space-between;

    .issue-section {
      cursor: pointer;
      display: flex;
    }
  }
`;

export const MaterialNavigationSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 3rem;
  margin-bottom: 5rem;
  animation: ${fadeIn} 0.5s;
  padding-bottom: 3rem;

  @media (${({ theme }) => theme.MEDIA_MIN.tablet}) {
    justify-content: flex-end;
    padding-bottom: 0;
  }

  & > button {
    margin-right: 2rem;
    &:last-child {
      margin-right: 0;
    }
  }
`;

export const ReadButton = styled(SecondaryModernButton)<{ active: boolean }>`
  ${({ active }) =>
    active &&
    css`
      color: ${({ theme }) => theme.UI_COLORS.light};
      background-color: ${({ theme }) => theme.UI_COLORS.success};
      box-shadow: inset 0 0 0 2px
        ${({ theme }) => theme.UI_COLORS.success}!important;

      svg {
        fill: ${({ theme }) => theme.UI_COLORS.light};
        margin-left: 1rem;
        height: 14px;
      }
    `}
`;

export const ImageZoomContainer = styled.div`
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
  cursor: zoom-out;
`;
